/**
 * Página de Configurações
 * Permite que médicos e administradores gerenciem suas preferências e dados.
 * - Médicos: Perfil clínico e documentos profissionais
 * - Admin: Perfil, integração Mercado Pago, e ferramentas de desenvolvimento
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import {
  DoctorAvatarSettings,
  DoctorBiographySettings,
  DoctorIdentitySettings,
  DoctorClinicSettings,
} from '@/components/dashboard';
import {
  Settings,
  Loader2,
  AlertCircle,
  FileText,
  Sliders,
  User,
  CreditCard,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Building2,
  ShieldAlert,
} from 'lucide-react';
import { getDoctorByUserId, getAdminByUserId } from '@/services/index';
import type { DoctorDTO, AdminDTO } from '@/services/index';
import { DoctorDocumentsManager } from '@/components/documents/DoctorDocumentsManager';
import { AccountCredentialsSection } from '@/components/auth/AccountCredentialsSection';
import { getMercadoPagoStatus, getResetStatus, resetSystemData, type ResetStatus } from '@/services/mercado-pago';
import type { MercadoPagoIntegrationStatus } from '@/services/payments';
import { toast } from 'sonner';
import { PATIENT_PROFILE_PATH } from '@/lib/patientEditableProfile';

type DoctorTab = 'config' | 'documents';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout, updateSessionUser } = useAuth();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [doctorProfile, setDoctorProfile] = useState<DoctorDTO | null>(null);
  const [adminProfile, setAdminProfile] = useState<AdminDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DoctorTab>('config');

  // Mercado Pago state (admin only)
  const [mpStatus, setMpStatus] = useState<MercadoPagoIntegrationStatus | null>(null);
  const [resetStatus, setResetStatus] = useState<ResetStatus | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [lastResetResult, setLastResetResult] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Paciente: dados clínicos/cadastrais ficam só em Meu Perfil
  useEffect(() => {
    if (!isLoading && user?.role === UserRole.PATIENT) {
      navigate(PATIENT_PROFILE_PATH, { replace: true });
    }
  }, [user, isLoading, navigate]);

  // Load data based on user role
  useEffect(() => {
    if (!user) return;
    if (user.role === UserRole.PATIENT) return;

    const loadData = async () => {
      setIsLoadingData(true);
      setError(null);

      try {
        if (user.role === UserRole.DOCTOR) {
          const doctorResponse = await getDoctorByUserId(user.id);
          if (doctorResponse.success && doctorResponse.data) {
            setDoctorProfile(doctorResponse.data);
          } else {
            throw new Error(doctorResponse.error || 'Falha ao carregar perfil do médico');
          }
        } else if (user.role === UserRole.ADMIN) {
          // Load admin profile
          const adminResponse = await getAdminByUserId(user.id);
          if (adminResponse.success && adminResponse.data) {
            setAdminProfile(adminResponse.data);
          }

          // Load Mercado Pago and Reset status
          try {
            const [mpData, resetRes] = await Promise.all([
              getMercadoPagoStatus(),
              getResetStatus(),
            ]);
            setMpStatus(mpData);
            if (resetRes.success && resetRes.data) {
              setResetStatus(resetRes.data);
            }
          } catch {
            // fallback
          }
        }
      } catch (err) {
        console.error('Error loading settings data:', err);
        setError('Erro ao carregar dados de configurações.');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleResetData = async () => {
    if (resetConfirm !== 'RESET') {
      toast.error('Digite "RESET" para confirmar a operação.');
      return;
    }

    setIsResetting(true);
    try {
      const result = await resetSystemData();
      if (result.success && result.data) {
        toast.success('Reset concluído! Dados de teste removidos com sucesso.');
        setLastResetResult(result.data.deletedCounts || result.data.counts || null);
        setShowResetConfirm(false);
        setResetConfirm('');
      } else {
        toast.error((result as any).error || 'Erro ao executar reset.');
      }
    } catch (err) {
      console.error('Reset error:', err);
      toast.error('Erro ao conectar com o servidor para reset.');
    } finally {
      setIsResetting(false);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col">
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6">
          <div className="flex items-center gap-3">
            <Settings className="w-8 h-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Configurações
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {user.role === UserRole.ADMIN
                  ? 'Gerencie o perfil, integrações e ferramentas do sistema.'
                  : 'Gerencie suas preferências e informações profissionais.'}
              </p>
            </div>
          </div>

          {/* Tabs — only visible for doctors */}
          {user.role === UserRole.DOCTOR && (
            <div className="flex gap-1 mt-6 border-b border-gray-200 dark:border-neutral-800 -mb-6">
              <button
                onClick={() => setActiveTab('config')}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === 'config'
                    ? 'border-teal-600 text-teal-600 dark:text-teal-400 dark:border-teal-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Sliders className="w-4 h-4" />
                Perfil
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === 'documents'
                    ? 'border-teal-600 text-teal-600 dark:text-teal-400 dark:border-teal-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4" />
                Documentos
              </button>
            </div>
          )}
        </header>

        <main className="flex-1 p-8 space-y-8 bg-gray-50 dark:bg-neutral-950 overflow-auto">
          {error && (
            <div className="bg-amber-50 p-4 rounded-lg flex gap-3 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}

          {isLoadingData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
            </div>
          ) : user.role === UserRole.DOCTOR ? (
            <>
              {/* Config tab */}
              {activeTab === 'config' && (
                <div className="max-w-4xl space-y-8">
                  {doctorProfile && (
                    <>
                      <DoctorAvatarSettings
                        doctor={doctorProfile}
                        onUpdate={(updated) => setDoctorProfile(updated)}
                      />
                      <DoctorIdentitySettings
                        doctor={doctorProfile}
                        onUpdate={(updated) => setDoctorProfile(updated)}
                      />
                      <DoctorBiographySettings
                        doctor={doctorProfile}
                        onUpdate={(updated) => setDoctorProfile(updated)}
                      />
                      <DoctorClinicSettings
                        doctor={doctorProfile}
                        onUpdate={(updated) => setDoctorProfile(updated)}
                      />
                    </>
                  )}
                </div>
              )}

              {/* Documents tab */}
              {activeTab === 'documents' && doctorProfile && (
                <div className="max-w-4xl space-y-6">
                  <DoctorDocumentsManager doctorId={doctorProfile.id} />
                  <AccountCredentialsSection
                    email={user.email}
                    onEmailUpdated={updateSessionUser}
                  />
                </div>
              )}
            </>
          ) : user.role === UserRole.ADMIN ? (
            <div className="max-w-4xl space-y-6">

              {/* ── Card: Perfil do Administrador ── */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-neutral-800">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-xl text-teal-600">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-gray-900 dark:text-white">Perfil do Administrador</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Informações da sua conta de administrador</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-400 uppercase">Nome</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{user.name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-400 uppercase">E-mail</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{user.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-400 uppercase">Departamento</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {adminProfile?.department || 'Não definido'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-400 uppercase">Status</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      adminProfile?.isActive !== false
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    }`}>
                      <CheckCircle className="w-3 h-3" />
                      {adminProfile?.isActive !== false ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Card: Integração Mercado Pago ── */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-neutral-800">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-sky-50 dark:bg-sky-900/20 rounded-xl text-sky-600">
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-gray-900 dark:text-white">Integração de Pagamentos (Mercado Pago)</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Configure o Checkout Pro e PIX via Mercado Pago</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {/* Status badge */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-neutral-800/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      {mpStatus?.configured ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {mpStatus?.configured ? 'Mercado Pago Configurado' : 'Modo Desenvolvimento (Mock)'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {mpStatus?.configured
                            ? 'Access Token detectado. Pagamentos reais ativos.'
                            : 'Defina MERCADOPAGO_ACCESS_TOKEN no backend para habilitar pagamentos reais.'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      mpStatus?.configured
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                    }`}>
                      {mpStatus?.configured ? 'ATIVO' : 'MOCK'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-4 border border-gray-200 dark:border-neutral-700 rounded-xl">
                      <p className="text-xs text-gray-400 uppercase font-medium mb-1">Ambiente</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white capitalize">
                        {mpStatus?.environment || 'development'}
                      </p>
                    </div>
                    <div className="p-4 border border-gray-200 dark:border-neutral-700 rounded-xl">
                      <p className="text-xs text-gray-400 uppercase font-medium mb-1">Access Token Backend</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {mpStatus?.accessTokenPresent ? '✓ Configurado' : '✗ Não configurado'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://www.mercadopago.com.br/developers/panel/app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Painel Mercado Pago Developers
                    </a>
                  </div>

                  <div className="p-4 bg-sky-50 dark:bg-sky-900/10 border border-sky-100 dark:border-sky-900/30 rounded-xl text-sm text-sky-800 dark:text-sky-300">
                    <p className="font-semibold mb-1">Como configurar:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Acesse o Painel Developers do Mercado Pago e crie uma aplicação</li>
                      <li>Obtenha seu Access Token de Teste ou Produção (ex: <code className="bg-sky-100 dark:bg-sky-900/40 px-1 rounded">APP_USR-...</code>)</li>
                      <li>Adicione <code className="bg-sky-100 dark:bg-sky-900/40 px-1 rounded">MERCADOPAGO_ACCESS_TOKEN=APP_USR-...</code> no <code className="bg-sky-100 dark:bg-sky-900/40 px-1 rounded">.env</code> do backend</li>
                      <li>O frontend usará Checkout Pro por redirecionamento automático (PIX, Cartão, Boleto)</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* ── Card: Ambiente de Testes (Reset) ── */}
              {resetStatus?.available && (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-red-200 dark:border-red-900/50 overflow-hidden">
                  <div className="p-6 border-b border-red-100 dark:border-red-900/30 bg-red-50 dark:bg-red-950/20">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl text-red-600">
                        <ShieldAlert className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-red-900 dark:text-red-300">Ambiente de Testes — Reset de Dados</h2>
                        <p className="text-sm text-red-600 dark:text-red-400">Disponível apenas em: <strong>{resetStatus.environment}</strong></p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800 dark:text-amber-300">
                        <p className="font-bold mb-1">⚠️ Atenção — Operação Irreversível</p>
                        <p>O reset remove <strong>todos</strong> os dados de teste: médicos, pacientes, consultas, documentos e avaliações. Apenas administradores são preservados. Use apenas para testes críticos.</p>
                      </div>
                    </div>

                    {lastResetResult && (
                      <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 rounded-xl">
                        <p className="text-sm font-bold text-green-800 dark:text-green-300 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Último Reset — Registros Removidos
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {Object.entries(lastResetResult).map(([key, count]) => (
                            <div key={key} className="text-xs bg-green-100 dark:bg-green-900/30 rounded-lg px-2 py-1">
                              <span className="text-green-700 dark:text-green-300 font-medium capitalize">{key}:</span>{' '}
                              <span className="text-green-800 dark:text-green-200 font-bold">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!showResetConfirm ? (
                      <button
                        onClick={() => setShowResetConfirm(true)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Iniciar Reset de Dados
                      </button>
                    ) : (
                      <div className="space-y-3 p-4 border-2 border-red-300 dark:border-red-800 rounded-xl">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          Para confirmar, digite <code className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded font-mono">RESET</code> abaixo:
                        </p>
                        <input
                          type="text"
                          placeholder="Digite RESET para confirmar"
                          value={resetConfirm}
                          onChange={(e) => setResetConfirm(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500 font-mono"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleResetData}
                            disabled={isResetting || resetConfirm !== 'RESET'}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-colors"
                          >
                            {isResetting ? (
                              <><RefreshCw className="w-4 h-4 animate-spin" /> Executando reset...</>
                            ) : (
                              <><Trash2 className="w-4 h-4" /> Confirmar Reset</>
                            )}
                          </button>
                          <button
                            onClick={() => { setShowResetConfirm(false); setResetConfirm(''); }}
                            className="px-4 py-2 border border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Se reset não disponível (produção) */}
              {resetStatus && !resetStatus.available && (
                <div className="p-4 bg-gray-50 dark:bg-neutral-800/50 border border-gray-200 dark:border-neutral-700 rounded-xl text-sm text-gray-500 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Reset de dados indisponível em ambiente de produção.
                </div>
              )}

            </div>
          ) : (
            // Paciente é redirecionado para /perfil — fallback sem formulário duplicado
            <div className="max-w-4xl mx-auto py-16 text-center">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-500 text-sm">Redirecionando para Meu Perfil...</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
